import React, { useContext, useEffect, useState } from 'react';
import { bool, number, func } from 'prop-types';
import classNames from 'classnames/bind';
import Sticky from 'react-stickynode';

import useStyles from 'hooks/useStyles';
import PlayList from 'routes/course/components/PlayList';
import CourseContext from 'routes/course/components/CourseContext';
import PaymentOptionList from 'components/PaymentOptionList';
import GetFreeTrialLink from 'components/GetFreeTrialLink';

import { COURSE } from 'constants/contentTypes';

import ContinueLearningCTA from '../ContinueLearningCTA';

// eslint-disable-next-line css-modules/no-unused-class
import styles from './PlayListAndPaymentList.scss';

const cx = classNames.bind(styles);

function PlayListAndPaymentList({
  isShowPaymentList,
  isLessonPage,
  isLearningPage,
  goCourseLearningPage,
  showContinueLearningSection,
  showPromotion,
  stickyBottomBoundary,
}) {
  useStyles(styles);

  const {
    id,
    isLessonListOpen,
    lessonList,
    freeLessonList,
    isPreOrdered,
    enrollStartAt,
    learningProgress,
    nextLessonForPurchasedUser,
    userPurchased,
    title,
  } = useContext(CourseContext);

  const [playListHeight, setPlayListHeight] = useState();

  useEffect(() => {
    function observeVideoHeight() {
      const isCourseDetailPageForFree =
        !isLessonPage && !isLearningPage && !userPurchased;

      if (isCourseDetailPageForFree) return;

      const videoEl = document.getElementById('videoWrapper');

      const observer = new ResizeObserver(([evt]) => {
        setPlayListHeight((evt.target.offsetWidth / 16) * 9);
      });

      observer.observe(videoEl);

      // eslint-disable-next-line consistent-return
      return () => {
        observer.unobserve(videoEl);
      };
    }

    const unobserveFunction = observeVideoHeight();

    return () => {
      if (unobserveFunction) unobserveFunction();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLessonPage, isLearningPage]);

  return (
    <div className={styles.root}>
      <div className={styles.content}>
        <div
          style={{ height: `${playListHeight}px` }}
          className={cx(styles.playList)}
        >
          {isLessonListOpen && (
            <PlayList
              isLearningPage={isLearningPage}
              lessonList={
                isLessonPage
                  ? lessonList.filter(lesson => lesson.id)
                  : lessonList
              }
              freeLessonList={
                isLessonPage
                  ? freeLessonList.filter(lesson => lesson.id)
                  : freeLessonList
              }
              isLessonPage={isLessonPage}
            />
          )}
        </div>
        {isShowPaymentList && (
          <div>
            <GetFreeTrialLink
              isShow={isLessonPage}
              className={styles.getFreeTrial}
              type="course"
              id={id}
              name={title}
              section="lesson_syllabus"
            />
            {showContinueLearningSection && (
              <ContinueLearningCTA
                isPreOrdered={isPreOrdered}
                enrollStartAt={enrollStartAt}
                courseLearningProgress={learningProgress}
                lessonInfo={nextLessonForPurchasedUser}
                onClick={goCourseLearningPage}
              />
            )}
            {showPromotion && (
              <Sticky
                top={110}
                bottomBoundary={stickyBottomBoundary - 80}
                shouldFreeze={() => stickyBottomBoundary === 0}
              >
                <div className={cx(styles.paymentOption)}>
                  <PaymentOptionList contentType={COURSE} contentId={id} />
                </div>
              </Sticky>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

PlayListAndPaymentList.propTypes = {
  isShowPaymentList: bool.isRequired,
  isLessonPage: bool,
  isLearningPage: bool,
  showContinueLearningSection: bool,
  showPromotion: bool,
  goCourseLearningPage: func,
  stickyBottomBoundary: number,
};

PlayListAndPaymentList.defaultProps = {
  isLessonPage: false,
  showPromotion: false,
  showContinueLearningSection: false,
  isLearningPage: false,
  goCourseLearningPage: () => {},
  stickyBottomBoundary: 0,
};

export default PlayListAndPaymentList;
